import React, { useEffect, useRef } from 'react';
import './StaticParticles.css';

const isMobile = window.innerWidth <= 768;

const StaticParticles = ({
  count = 150,
  color = '#FDB241',
  opacity = 0.6,
  sway = 0.5
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    const container = canvas.parentElement;

    let width = container.offsetWidth;
    let height = container.offsetHeight;

    canvas.width = width;
    canvas.height = height;

    // Парсим цвет один раз
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 253, g: 178, b: 65 };
    };
    const rgb = hexToRgb(color);

    // Pre-render glow sprite один раз — заменяет shadowBlur без потери внешнего вида
    const SPRITE_SIZE = 48;
    const sprite = document.createElement('canvas');
    sprite.width = SPRITE_SIZE;
    sprite.height = SPRITE_SIZE;
    const sCtx = sprite.getContext('2d');
    const center = SPRITE_SIZE / 2;
    const grad = sCtx.createRadialGradient(center, center, 0, center, center, center);
    grad.addColorStop(0,   `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`);
    grad.addColorStop(0.3, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`);
    grad.addColorStop(0.7, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`);
    grad.addColorStop(1,   `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
    sCtx.fillStyle = grad;
    sCtx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);

    // Снижаем количество частиц на мобиле
    const actualCount = isMobile ? Math.min(count, 80) : count;

    // Генерируем частицы
    const particles = [];
    for (let i = 0; i < actualCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      particles.push({
        baseX: x,
        baseY: y,
        x, y,
        radius: Math.random() * 2 + 0.5,
        baseOpacity: Math.random() * 0.4 + 0.3,
        pulseSpeed: Math.random() * 0.5 + 0.5,
        pulsePhase: Math.random() * Math.PI * 2,
        swayPhaseX: Math.random() * Math.PI * 2,
        swayPhaseY: Math.random() * Math.PI * 2,
        swaySpeedX: (Math.random() * 0.5 + 0.5) * 0.001,
        swaySpeedY: (Math.random() * 0.5 + 0.5) * 0.001
      });
    }

    // Resize
    const handleResize = () => {
      const newW = container.offsetWidth;
      const newH = container.offsetHeight;
      particles.forEach(p => {
        p.baseX = (p.baseX / width) * newW;
        p.baseY = (p.baseY / height) * newH;
        p.x = p.baseX;
        p.y = p.baseY;
      });
      width = newW;
      height = newH;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);

    // Intersection Observer — пауза когда canvas вне экрана
    let isVisible = true;
    const observer = new IntersectionObserver(
      ([entry]) => { isVisible = entry.isIntersecting; },
      { threshold: 0.05 }
    );
    observer.observe(canvas);

    // FPS cap: 20 на мобиле, 30 на десктопе
    let animId;
    let lastTime = 0;
    const fpsInterval = 1000 / (isMobile ? 20 : 30);

    const animate = (currentTime) => {
      animId = requestAnimationFrame(animate);

      if (!isVisible) return;

      const elapsed = currentTime - lastTime;
      if (elapsed < fpsInterval) return;
      lastTime = currentTime - (elapsed % fpsInterval);

      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // пульс
        p.pulsePhase += p.pulseSpeed * 0.02;
        const pulsation = Math.sin(p.pulsePhase) * 0.3;
        const finalOpacity = (p.baseOpacity + pulsation) * opacity;

        // sway
        if (sway > 0) {
          p.swayPhaseX += p.swaySpeedX;
          p.swayPhaseY += p.swaySpeedY;
          p.x = p.baseX + Math.sin(p.swayPhaseX) * 2 * sway;
          p.y = p.baseY + Math.cos(p.swayPhaseY) * 2 * sway;
        }

        // Glow через pre-rendered sprite (вместо shadowBlur)
        const drawSize = p.radius * 14;
        ctx.globalAlpha = finalOpacity * 0.75;
        ctx.drawImage(sprite, p.x - drawSize / 2, p.y - drawSize / 2, drawSize, drawSize);

        // Яркая точка в центре
        ctx.globalAlpha = finalOpacity * 0.8;
        ctx.fillStyle = `rgba(255, 255, 255, 1)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * 0.45, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    };

    animate(0);

    // Cleanup
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [count, color, opacity, sway]);

  return <canvas ref={canvasRef} className="static-particles-canvas" />;
};

export default StaticParticles;
